import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import type { ConnectionInfo } from "../types";
import { CopyButton } from "./CopyButton";

interface Props {
  connection: ConnectionInfo;
}

function Field({
  label,
  value,
  mono = true,
  masked = false,
  onToggleMask,
  isMasked,
}: {
  label: string;
  value: string;
  mono?: boolean;
  masked?: boolean;
  onToggleMask?: () => void;
  isMasked?: boolean;
}) {
  return (
    <div className="group bg-ink-900 border border-ink-700 hover:border-ink-600 transition-colors">
      <div className="flex items-center justify-between px-3 py-2 border-b border-ink-800">
        <span className="term-label">{label}</span>
        <div className="flex items-center gap-3">
          {masked && onToggleMask && (
            <button
              type="button"
              onClick={onToggleMask}
              className="text-ink-400 hover:text-phosphor-300"
              aria-label={isMasked ? "Reveal" : "Mask"}
            >
              {isMasked ? <Eye size={13} /> : <EyeOff size={13} />}
            </button>
          )}
          <CopyButton value={value} label="" size={12} />
        </div>
      </div>
      <div
        className={`px-3 py-3 ${
          mono ? "font-mono" : ""
        } text-sm text-ink-100 break-all`}
      >
        {masked && isMasked ? "•".repeat(Math.min(value.length, 24)) : value}
      </div>
    </div>
  );
}

export function ConnectionCard({ connection }: Props) {
  const [passMasked, setPassMasked] = useState(true);

  const psqlCmd = `psql "${connection.dsn}"`;
  const sqlalchemyUrl = connection.dsn.replace(
    /^postgres(ql)?:\/\//,
    "postgresql+psycopg://"
  );
  const envBlock = `PGHOST=${connection.host}
PGPORT=${connection.port}
PGUSER=${connection.user}
PGPASSWORD=${connection.password}
PGDATABASE=${connection.database}
DATABASE_URL=${connection.dsn}`;
  const pyPsycopg = `import psycopg

conn = psycopg.connect("${connection.dsn}")
with conn.cursor() as cur:
    cur.execute("select 1")
    print(cur.fetchone())`;

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="term-label">credentials</div>
          <CopyButton value={connection.dsn} label="copy dsn" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="host" value={connection.host} />
          <Field label="port" value={String(connection.port)} />
          <Field label="database" value={connection.database} />
          <Field label="user" value={connection.user} />
          <Field
            label="password"
            value={connection.password}
            masked
            isMasked={passMasked}
            onToggleMask={() => setPassMasked((v) => !v)}
          />
          <Field label="dsn" value={connection.dsn} />
        </div>
      </div>

      <div>
        <div className="term-label mb-3">snippets</div>
        <div className="space-y-3">
          <Snippet title="psql" code={psqlCmd} />
          <Snippet title="sqlalchemy url" code={sqlalchemyUrl} />
          <Snippet title=".env" code={envBlock} />
          <Snippet title="python psycopg" code={pyPsycopg} />
        </div>
      </div>
    </div>
  );
}

function Snippet({ title, code }: { title: string; code: string }) {
  return (
    <div className="bracket-frame !p-0 !bg-ink-850">
      <span className="bracket-bl" />
      <span className="bracket-br" />
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink-800">
        <span className="term-label">{title}</span>
        <CopyButton value={code} />
      </div>
      <pre className="px-4 py-3 text-xs leading-relaxed text-ink-200 overflow-x-auto whitespace-pre">
{code}
      </pre>
    </div>
  );
}
